// Generic paged texture / animation gallery used by the ENEMIES, BOSSES, ICONS
// and ENVIRONMENT tabs. Entries are discovered from the texture manager by key
// prefix so the preview never depends on another module's internals.

import Phaser from 'phaser';
import { COLORS } from '../../config/palette';
import { Button, drawPanel, label } from './widgets';

export interface GalleryEntry {
  key: string;
  frame?: string | number;
  /** Phaser animation key to play instead of a static frame. */
  anim?: string;
  label: string;
  sub?: string;
  tint?: number;
  /** Draw additively (glow / emissive layers). */
  add?: boolean;
}

export interface GalleryOpts {
  x: number;
  y: number;
  w: number;
  h: number;
  cellW: number;
  cellH: number;
  /** Upper bound on the fit scale (1 = never upscale beyond native size). */
  maxScale?: number;
  accent?: number;
  title?: string;
}

/** Texture keys starting with any prefix (excluding Phaser internals), sorted. */
export function texturesWithPrefix(scene: Phaser.Scene, prefixes: readonly string[], exclude: readonly string[] = []): string[] {
  return scene.textures
    .getTextureKeys()
    .filter((k) => prefixes.some((p) => k.startsWith(p)) && !exclude.some((e) => k.startsWith(e)))
    .sort();
}

/** Every registered animation whose first frame uses the texture. */
export function animsForTexture(scene: Phaser.Scene, key: string): Phaser.Animations.Animation[] {
  return scene.anims
    .getAnimsFromTexture(key)
    .map((k) => scene.anims.get(k))
    .filter((a): a is Phaser.Animations.Animation => !!a && a.frames.length > 0 && a.frames[0].textureKey === key)
    .sort((a, b) => a.key.localeCompare(b.key));
}

/** Named frames of a texture except the base frame. */
export function frameNames(scene: Phaser.Scene, key: string): string[] {
  if (!scene.textures.exists(key)) return [];
  return scene.textures
    .get(key)
    .getFrameNames(false)
    .filter((n) => n !== '__BASE');
}

export class Gallery {
  readonly root: Phaser.GameObjects.Container;
  private items: Phaser.GameObjects.Container;
  private pageText: Phaser.GameObjects.Text;
  private page = 0;
  private sprites: { s: Phaser.GameObjects.Sprite; anim: string; loop: boolean; wait: number }[] = [];
  private readonly perPage: number;
  private readonly cols: number;

  constructor(
    private scene: Phaser.Scene,
    private entries: GalleryEntry[],
    private o: GalleryOpts,
  ) {
    this.root = scene.add.container(0, 0);
    this.items = scene.add.container(0, 0);
    const g = scene.add.graphics();
    drawPanel(g, o.x, o.y, o.w, o.h, { fill: COLORS.uiBg, fillAlpha: 0.75, chamfer: 12, accent: o.accent });
    this.root.add(g);
    if (o.title) this.root.add(label(scene, o.x + 16, o.y + 10, o.title, { font: 'display', size: 15, weight: 700, spacing: 3 }));
    this.cols = Math.max(1, Math.floor((o.w - 24) / o.cellW));
    const rows = Math.max(1, Math.floor((o.h - 64) / o.cellH));
    this.perPage = this.cols * rows;
    this.pageText = label(scene, o.x + o.w - 120, o.y + 14, '', { font: 'mono', size: 12, color: COLORS.uiDim, originX: 1 });
    const prev = new Button(scene, o.x + o.w - 110, o.y + 8, '<', () => this.flip(-1), { w: 44, h: 28, accent: o.accent });
    const next = new Button(scene, o.x + o.w - 58, o.y + 8, '>', () => this.flip(1), { w: 44, h: 28, accent: o.accent });
    this.root.add([this.items, this.pageText, prev.root, next.root]);
    this.render();
  }

  get pages(): number {
    return Math.max(1, Math.ceil(this.entries.length / this.perPage));
  }

  flip(d: number): void {
    this.page = (this.page + d + this.pages) % this.pages;
    this.render();
  }

  setEntries(entries: GalleryEntry[]): void {
    this.entries = entries;
    this.page = 0;
    this.render();
  }

  private render(): void {
    const s = this.scene;
    const o = this.o;
    this.items.removeAll(true);
    this.sprites = [];
    this.pageText.setText(`${this.entries.length} ITEMS · PAGE ${this.page + 1}/${this.pages}`);
    if (this.entries.length === 0) {
      this.items.add(label(s, o.x + o.w / 2, o.y + o.h / 2, 'NOTHING REGISTERED YET', { font: 'display', size: 18, color: COLORS.uiDim, originX: 0.5, originY: 0.5, spacing: 4 }));
      return;
    }
    const slice = this.entries.slice(this.page * this.perPage, (this.page + 1) * this.perPage);
    const grid = s.add.graphics();
    this.items.add(grid);
    const gx = o.x + 12 + (o.w - 24 - this.cols * o.cellW) / 2;
    slice.forEach((e, i) => {
      const cx = gx + (i % this.cols) * o.cellW;
      const cy = o.y + 52 + Math.floor(i / this.cols) * o.cellH;
      grid.lineStyle(1, COLORS.uiBorder, 0.5);
      grid.strokeRect(cx + 3, cy + 3, o.cellW - 6, o.cellH - 6);
      const boxW = o.cellW - 16;
      const boxH = o.cellH - 40;
      const mid = { x: cx + o.cellW / 2, y: cy + 8 + boxH / 2 };
      if (!s.textures.exists(e.key)) {
        this.items.add(label(s, mid.x, mid.y, 'MISSING', { font: 'mono', size: 11, color: COLORS.uiBad, originX: 0.5, originY: 0.5 }));
      } else {
        const spr = s.add.sprite(mid.x, mid.y, e.key, e.frame);
        const fw = spr.frame?.realWidth || spr.width || 1;
        const fh = spr.frame?.realHeight || spr.height || 1;
        spr.setScale(Math.min(o.maxScale ?? 1, boxW / fw, boxH / fh));
        if (e.tint !== undefined) spr.setTint(e.tint);
        if (e.add) spr.setBlendMode(Phaser.BlendModes.ADD);
        if (e.anim && s.anims.exists(e.anim)) {
          const a = s.anims.get(e.anim);
          spr.play(e.anim);
          this.sprites.push({ s: spr, anim: e.anim, loop: a.repeat === -1, wait: 0 });
        }
        this.items.add(spr);
      }
      this.items.add(label(s, cx + o.cellW / 2, cy + o.cellH - 30, e.label, { font: 'mono', size: 11, color: COLORS.uiText, originX: 0.5 }));
      if (e.sub) this.items.add(label(s, cx + o.cellW / 2, cy + o.cellH - 17, e.sub, { font: 'mono', size: 10, color: COLORS.uiDim, originX: 0.5 }));
    });
  }

  /** Replays one-shot animations after a short pause so everything keeps moving. */
  update(dt: number): void {
    for (const it of this.sprites) {
      if (it.loop || it.s.anims.isPlaying) continue;
      it.wait += dt;
      if (it.wait > 0.8) {
        it.wait = 0;
        it.s.play(it.anim);
      }
    }
  }

  destroy(): void {
    this.root.destroy();
  }
}
